// The twelve packages the Corporate page offers, in the reference's three
// tabs. Every one exists in the real eDOS catalogue — the reference's
// names matched ours exactly — so the cards link to the real package
// page instead of being decoration.
//
// `params` is counted from each package's `components` string with the
// same rule as utils/mapApiDetail.js splitParameters, `/,(?!\s*\d)/`,
// which keeps names like "1,25-Dihydroxy Vitamin D3" in one piece. The
// reference's own counts are different and invented; these are ours.
//
// Counted on 2026-11-04. The packages carry no price in eDOS, which is
// fine here: the reference's cards show parameters, not prices, and
// corporate pricing is per-employee and quoted in the proposal.
//
// One number looks wrong and is worth checking: Elite Full-Body Check
// reports 29 parameters while Essential — the entry tier — reports 76.
// See API-NOTES 2.42.

export const PACKAGE_TABS = [
  {
    key: 'level',
    label: 'By level',
    items: [
      { id: 2583, for: 'Entry level', name: 'Essential Full-Body Check', q: 'Is anything obviously wrong that I can act on today?', params: 76 },
      { id: 2582, for: 'Most teams',  name: 'Signature Full-Body Check', q: 'Basic, plus: is my kidney quietly leaking, and am I deficient?', params: 82 },
      { id: 2581, for: 'Managers',    name: 'Premium Full-Body Check',   q: 'Essential, plus: the organ systems that fail silently in mid-life', params: 96 },
      { id: 2580, for: 'Leadership',  name: 'Elite Full-Body Check',     q: 'Comprehensive, plus: autoimmune, coagulation, endocrine axis and fibrosis', params: 29 },
    ],
  },
  {
    key: 'role',
    label: 'By role',
    items: [
      { id: 2608, for: 'Desk & IT teams',       name: 'IT Professional Screen',       q: 'Sedentary, screen-bound, sleep-deprived — what has it cost so far?', params: 91 },
      { id: 2606, for: 'High-pressure roles',   name: 'Stress & Burnout Assessment',  q: 'Is exhaustion adrenal, thyroid, metabolic or nutritional?', params: 70 },
      { id: 2607, for: 'Field & physical work', name: 'Athlete Check',                q: 'Is training load being absorbed, or is this over-reaching?', params: 81 },
      { id: 2595, for: 'Shift workers',         name: 'MetabolicIQ',                  q: 'How insulin-resistant am I, and what has it damaged?', params: 91 },
    ],
  },
  {
    key: 'age',
    label: 'By age & gender',
    items: [
      { id: 2585, for: 'Under 30', name: 'Young Adult Kickstart',     q: 'What is my baseline, and am I carrying anything I don’t know about?', params: 85 },
      { id: 2586, for: '40+',      name: 'Mid-Life Milestone (40+)',  q: 'At 40, which of the silent risks have started?', params: 91 },
      { id: 2589, for: 'Women',    name: 'Women’s Health Check', q: 'Am I well — with the things that go wrong in women checked properly?', params: 86 },
      { id: 2593, for: 'Men 30+',  name: 'Men’s Vitality (30+)', q: 'Is low energy hormonal, metabolic or neither?', params: 98 },
    ],
  },
];

export const PROGRAMMES = [
  { n: '01', t: 'Annual health checks',      d: 'A yearly check for every employee, matched to age and role. Booked at home, at a Care Centre or at the office.', bg: 'var(--tl-g9)',     fg: '#FFFFFF',       num: 'var(--tl-teal-l)', body: '#C7DDD7' },
  { n: '02', t: 'On-site health camps',      d: 'Trustians set up at your office or plant, collect samples by appointment slot, and leave without disrupting the working day.', bg: 'var(--tl-teal)',   fg: '#FFFFFF',       num: 'var(--tl-g9)',     body: '#E6FAF5' },
  { n: '03', t: 'Pre-employment screening',  d: 'Fixed test panels for new joiners, with fit-to-join status tracked for HR in one place.', bg: '#FFFFFF',          fg: 'var(--tl-g9)',  num: 'var(--tl-teal-d)', body: 'var(--tl-muted)' },
  { n: '04', t: 'Family & remote cover',     d: 'Home collection for employees working remotely, and optional checks for their parents and spouses.', bg: 'var(--tl-yellow)', fg: 'var(--tl-g9)',  num: 'var(--tl-g9)',     body: '#3A4020' },
];

export const HOW_IT_WORKS = [
  { n: '01', t: 'Share your team',           d: 'Headcount, sites, and any tests your policy requires.' },
  { n: '02', t: 'We build the plan',         d: 'Packages by level or role, camp dates and a per-employee price.' },
  { n: '03', t: 'Employees book',            d: 'Through a private link: at home, at a Care Centre or at the camp.' },
  { n: '04', t: 'Reports and doctor calls',  d: 'Same-day reports, with a free doctor call for flagged results.' },
  { n: '05', t: 'HR summary',                d: 'Participation and anonymised trends to plan next year.' },
];

export const CORPORATE_FAQS = [
  ['Does HR see individual employee results?',
   'No. Each employee receives their own report. HR sees participation and anonymised trends only, unless an employee gives consent or a statutory fitness record is required.'],
  ['Can you cover employees in different cities?',
   'Yes. Employees can book at any of our 14 branches or at home where we collect, and you receive one consolidated invoice.'],
  ['How quickly do employees get reports?',
   'Most routine reports are sent the same day for samples collected before midday. Specialty tests take longer; the time is shown on each test.'],
  ['What happens if a result is abnormal?',
   'The employee gets a free call with a TrustLab lab doctor to explain the result and what to discuss with their own physician.'],
  ['Is there a minimum team size?',
   'Home and Care Centre programmes work for any team size. On-site camps have a minimum headcount per site per day, which we confirm in your proposal.'],
  ['How is billing handled?',
   'One GST invoice per programme, at a per-employee rate agreed in your proposal. Payment terms are set out there too.'],
];

// The admin panel stores programme codes rather than labels so the
// wording can change without breaking old records — see the note on the
// CorporateInquiry model.
export const NEEDS = [
  { code: 'AHC',   label: 'Annual health checks' },
  { code: 'Camp',  label: 'On-site camp' },
  { code: 'PEM',   label: 'Pre-employment screening' },
  { code: 'EHP',   label: 'Family cover' },
];

export const TEAM_SIZES = ['1–50', '51–200', '201–1,000', '1,000+'];

export const CREDENTIALS = [
  ['NABL', 'ISO 15189:2022 accredited lab'],
  ['14', 'Branches for multi-city teams'],
  ['Same day', 'Routine reports, samples before midday'],
  ['1 invoice', 'For every site and employee'],
];
